/**
 * Settings → Jimi (ENG-201). Staff only.
 *
 * Turns Jimi on or off for the tenant you're signed into, and shows what it's doing
 * here: its access, its runs (watch and steer them) and every credential it was given.
 *
 * "On" is three grants in datahub (src/services/agents/jimi.py), and the Harness
 * Service reads them to decide which tenants it serves, so the switch takes effect
 * within a minute with no deploy. Every call here is for this tenant only; the
 * backend enforces that, this page just never asks for anything else.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ApiService from '@/services/api.service';
import { useApiAuthReady } from '@/hooks/useApiAuthReady';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2 } from 'lucide-react';
import { controlErrorText as errorText, formatDateTime } from '@/pages/agents/data';
import { RunSheet, Spinner } from '@/pages/agents/RunView';

type Grant = {
  id: string;
  agent: string;
  scopes: string[];
  post_as: string | null;
  created_by: string;
  created_at: string;
  expires_at: string | null;
  note: string | null;
};
type Identity = { agent: string; scopes: string[]; grant: Grant | null };
type JimiStatus = {
  enabled: boolean;
  up_to_date: boolean;
  grants: Identity[];
  runs_available: boolean;
  result?: { created?: number; revoked?: number; runs_ended?: number; runs_failed?: number };
};
type Mint = {
  id: string;
  agent: string;
  purpose: 'run' | 'publish';
  request_ids: string[];
  scopes: string[];
  caller_arn: string;
  minted_at: string;
  expires_at: string;
  snowflake_pat_name: string | null;
  git_repo: string | null;
  failed_at: string | null;
  revoked_at: string | null;
};
type Run = {
  id: string;
  agent?: string;
  tickets: string[];
  round?: string;
  startedAt?: string;
  status: string;
  lastActivityAt?: string;
  costUsd: number;
  sandboxLive: boolean;
};

const BASE = '/agent-platform/jimi';
const MINTS_PAGE = 25;
const AGENTS = ['jimi', 'jimi-triage', 'jimi-build'];

// What each identity is for, in the words staff use.
const IDENTITY: Record<string, string> = {
  jimi: 'Replies and status changes on requests, as “Jimi”',
  'jimi-triage': 'Triage and questions: reads the platform, requests, Snowflake and the repo',
  'jimi-build': 'Builds: the same, plus Ellie, uploads and publishing to Develop',
};

const api = () => ApiService.getInstance();

export default function JimiTab() {
  const apiReady = useApiAuthReady();
  const queryClient = useQueryClient();
  const [confirmOff, setConfirmOff] = useState(false);
  const [openRun, setOpenRun] = useState<string | null>(null);

  const status = useQuery<JimiStatus>({
    queryKey: ['jimi', 'status'],
    queryFn: async () => (await api().request('GET', BASE)) as JimiStatus,
    enabled: apiReady,
  });

  const toggle = useMutation({
    mutationFn: async (enabled: boolean) => (await api().request('PUT', BASE, { enabled })) as JimiStatus,
    onSuccess: (data) => {
      queryClient.setQueryData(['jimi', 'status'], data);
      queryClient.invalidateQueries({ queryKey: ['jimi', 'mints'] });
      queryClient.invalidateQueries({ queryKey: ['jimi', 'runs'] });
    },
  });

  if (status.isLoading || !apiReady) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (status.error || !status.data) {
    return <p className="text-sm text-destructive">{errorText(status.error, 'Could not load Jimi for this account.')}</p>;
  }
  const st = status.data;

  return (
    <div className="max-w-5xl space-y-8">
      {/* ── On / off ───────────────────────────────────────────── */}
      <section className="space-y-3">
        <div className="flex items-start justify-between gap-6">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold">
              Jimi
              {st.enabled ? <Badge variant="success">On</Badge> : <Badge variant="secondary">Off</Badge>}
            </h2>
            <p className="text-sm text-muted-foreground">
              Jimi triages this account's support requests, answers questions about them and builds approved
              changes. Turning it on lets the Harness Service work here within a minute; turning it off stops it
              at once and ends anything it's running.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2 pt-1">
            {toggle.isPending && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            <Switch
              checked={st.enabled}
              disabled={toggle.isPending}
              onCheckedChange={(on) => (on ? toggle.mutate(true) : setConfirmOff(true))}
              aria-label="Jimi on for this account"
            />
          </div>
        </div>

        {st.enabled && !st.up_to_date && (
          <div className="flex items-center justify-between gap-4 rounded-md border border-warning bg-warning/10 px-3 py-2 text-sm">
            <span>
              Jimi's access here was set up differently from what turning it on gives today
              {expiryOf(st) ? `, and expires on ${formatDate(expiryOf(st))}` : ''}. Updating replaces those
              grants (their runs end) and removes the expiry.
            </span>
            <Button size="sm" variant="outline" disabled={toggle.isPending} onClick={() => toggle.mutate(true)}>
              Update access
            </Button>
          </div>
        )}
        {toggle.error && <p className="text-sm text-destructive">{errorText(toggle.error, 'That didn’t work.')}</p>}
        {toggle.data?.result?.runs_failed ? (
          <p className="text-sm text-warning">
            {toggle.data.result.runs_failed} run(s) couldn’t remove their Snowflake token yet; the hourly sweep will.
          </p>
        ) : null}

        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Identity</th>
                <th className="px-3 py-2 font-medium">Access</th>
                <th className="px-3 py-2 font-medium">Granted</th>
                <th className="px-3 py-2 font-medium">Expires</th>
              </tr>
            </thead>
            <tbody>
              {st.grants.map((g) => (
                <tr key={g.agent} className="border-b align-top last:border-0">
                  <td className="px-3 py-2">
                    <div className="font-medium">{g.agent}</div>
                    <div className="text-xs text-muted-foreground">{IDENTITY[g.agent] ?? ''}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{(g.grant?.scopes ?? g.scopes).join(', ')}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {g.grant ? `${formatDateTime(g.grant.created_at)} by ${g.grant.created_by}` : 'Not granted'}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {g.grant ? (g.grant.expires_at ? formatDate(g.grant.expires_at) : 'Never') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <LinearAppSection />
      <RunsSection available={st.runs_available} onOpen={setOpenRun} />
      <ConnectionLog />

      <AlertDialog open={confirmOff} onOpenChange={setConfirmOff}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Turn Jimi off for this account?</AlertDialogTitle>
            <AlertDialogDescription>
              Jimi's credentials here stop working at once, anything it's running is ended, and it won't pick up
              new requests. You can turn it back on any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => toggle.mutate(false)}>Turn off</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RunSheet
        runId={openRun}
        onClose={() => setOpenRun(null)}
        base={`${BASE}/runs`}
        speaker="Jimi"
        resume={{ path: 'restart', body: { mode: 'resume' } }}
        steerPlaceholder="Direct Jimi (read at its next step), e.g. “Use the March export, not February’s.”"
        startOver
        onChanged={() => queryClient.invalidateQueries({ queryKey: ['jimi', 'runs'] })}
      />
    </div>
  );
}

// ── Linear app ────────────────────────────────────────────────────────────────

function LinearAppSection() {
  const install = useMutation({
    mutationFn: async () => (await api().request('POST', `${BASE}/linear/install`)) as { url: string },
    // Linear sends staff back to /settings/jimi/linear (JimiLinearInstall).
    onSuccess: ({ url }) => window.location.assign(url),
  });

  return (
    <section className="space-y-3">
      <div className="flex items-start justify-between gap-6">
        <div>
          <h2 className="text-base font-semibold">Linear app</h2>
          <p className="text-sm text-muted-foreground">
            Installs Jimi's Linear app in the workspace, once for every account, so it can be delegated tickets and
            mentioned. Reinstall after changing the app's scopes or if Linear revoked its access.
          </p>
        </div>
        <Button size="sm" variant="outline" className="shrink-0" disabled={install.isPending} onClick={() => install.mutate()}>
          Install in Linear
        </Button>
      </div>
      {install.error && <p className="text-sm text-destructive">{errorText(install.error, 'Couldn’t start the install.')}</p>}
    </section>
  );
}

function expiryOf(st: JimiStatus): string | null {
  const dates = st.grants.map((g) => g.grant?.expires_at).filter((d): d is string => !!d);
  return dates.length ? dates.sort()[0] : null;
}

// ── Runs ──────────────────────────────────────────────────────────────────────

function RunsSection({ available, onOpen }: { available: boolean; onOpen: (id: string) => void }) {
  const runs = useQuery<{ runs: Run[]; dispatch_paused: boolean }>({
    queryKey: ['jimi', 'runs'],
    queryFn: async () => (await api().request('GET', `${BASE}/runs`, { limit: 50 })) as { runs: Run[]; dispatch_paused: boolean },
    enabled: available,
    refetchInterval: 15_000,
  });

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-base font-semibold">Runs</h2>
        <p className="text-sm text-muted-foreground">
          What Jimi has worked on here, newest activity first. Open one to watch it or steer it.
        </p>
      </div>
      {!available ? (
        <p className="text-sm text-muted-foreground">Runs aren't available on this stack (it has no Harness Service).</p>
      ) : runs.isLoading ? (
        <Spinner />
      ) : runs.error ? (
        <p className="text-sm text-destructive">{errorText(runs.error, 'Could not reach the Harness Service.')}</p>
      ) : (
        <>
          {runs.data?.dispatch_paused && (
            <p className="text-sm text-warning">New work is paused on the Harness Service; runs already going carry on.</p>
          )}
          {runs.data?.runs.length ? (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="border-b bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Run</th>
                    <th className="px-3 py-2 font-medium">Tickets</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Last activity</th>
                    <th className="px-3 py-2 text-right font-medium">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.data.runs.map((r) => (
                    <tr key={r.id} className="cursor-pointer border-b last:border-0 hover:bg-muted/40" onClick={() => onOpen(r.id)}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{r.agent ?? 'run'}{r.round ? ` · ${r.round}` : ''}</div>
                        <div className="font-mono text-xs text-muted-foreground">{shortRunId(r.id)}</div>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{r.tickets.join(', ') || '—'}</td>
                      <td className="px-3 py-2">
                        <span className="flex items-center gap-1.5">
                          {r.status}
                          {r.sandboxLive && <Badge variant="info">sandbox live</Badge>}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{formatDateTime(r.lastActivityAt)}</td>
                      <td className="px-3 py-2 text-right text-muted-foreground">${r.costUsd.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No runs here yet.</p>
          )}
        </>
      )}
    </section>
  );
}

function shortRunId(id: string): string {
  // <stack>:<tenant>:<request>[:<round>] — the tenant is this one, so leave it out.
  const [, , ...rest] = id.split(':');
  return rest.join(':') || id;
}

// ── Connection log ────────────────────────────────────────────────────────────

function ConnectionLog() {
  const [pages, setPages] = useState<Mint[][]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  // Not polled: a refresh would drop the older pages someone loaded. Switching Jimi
  // on or off refetches it.
  const first = useQuery<Mint[]>({
    queryKey: ['jimi', 'mints'],
    queryFn: async () => {
      const data = (await api().request('GET', '/agent-token-mints', { agent: AGENTS.join(','), limit: MINTS_PAGE })) as { mints: Mint[] };
      setPages([]);
      return data.mints;
    },
  });
  const mints = [...(first.data ?? []), ...pages.flat()];
  const lastPage = pages.length ? pages[pages.length - 1] : first.data ?? [];

  const loadMore = async () => {
    const before = mints[mints.length - 1]?.minted_at;
    if (!before) return;
    setLoadingMore(true);
    try {
      const data = (await api().request('GET', '/agent-token-mints', { agent: AGENTS.join(','), limit: MINTS_PAGE, before })) as { mints: Mint[] };
      setPages((p) => [...p, data.mints]);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-base font-semibold">Connection log</h2>
        <p className="text-sm text-muted-foreground">
          Every time Jimi was given credentials for this account: for which requests, by which caller, and whether
          they've been ended. No secrets are stored here.
        </p>
      </div>
      {first.isLoading ? (
        <Spinner />
      ) : first.error ? (
        <p className="text-sm text-destructive">{errorText(first.error, 'Could not load the connection log.')}</p>
      ) : mints.length === 0 ? (
        <p className="text-sm text-muted-foreground">Jimi hasn't connected here yet.</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium">Identity</th>
                  <th className="px-3 py-2 font-medium">For</th>
                  <th className="px-3 py-2 font-medium">Caller</th>
                  <th className="px-3 py-2 font-medium">Credentials</th>
                  <th className="px-3 py-2 font-medium">State</th>
                </tr>
              </thead>
              <tbody>
                {mints.map((m) => (
                  <tr key={m.id} className="border-b align-top last:border-0">
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{formatDateTime(m.minted_at)}</td>
                    <td className="px-3 py-2">
                      {m.agent}
                      {m.purpose === 'publish' && <Badge variant="warning" className="ml-1.5">publish</Badge>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{m.request_ids.join(', ')}</td>
                    <td className="px-3 py-2 text-muted-foreground">{callerOf(m.caller_arn)}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{credentialsOf(m)}</td>
                    <td className="px-3 py-2">{stateOf(m)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {lastPage.length === MINTS_PAGE && (
            <Button variant="outline" size="sm" disabled={loadingMore} onClick={loadMore}>
              {loadingMore ? 'Loading…' : 'Older'}
            </Button>
          )}
        </>
      )}
    </section>
  );
}

function callerOf(arn: string): string {
  // arn:aws:sts::<account>:assumed-role/<role>/<session>
  const [, role = '', session = ''] = arn.split(':').pop()?.split('/') ?? [];
  if (role.startsWith('plb-agents-harness')) return 'Harness Service';
  if (role.startsWith('plb-agents-laptop')) return `Laptop (${session})`;
  return role || arn;
}

function credentialsOf(m: Mint): string {
  const out = ['Pliable'];
  if (m.snowflake_pat_name) out.push('Snowflake');
  if (m.git_repo) out.push(m.purpose === 'publish' ? 'repo (write)' : 'repo (read)');
  return out.join(', ');
}

function stateOf(m: Mint) {
  if (m.failed_at && !m.revoked_at) return <Badge variant="destructive">failed, cleaning up</Badge>;
  if (m.failed_at) return <Badge variant="secondary">failed</Badge>;
  if (m.revoked_at) return <Badge variant="secondary">ended</Badge>;
  if (new Date(m.expires_at).getTime() < Date.now() && !m.snowflake_pat_name) return <Badge variant="secondary">expired</Badge>;
  return <Badge variant="success">active</Badge>;
}

// ── Formatting ────────────────────────────────────────────────────────────────

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

